import { useEffect } from "react";

/**
 * Injects dynamic SEO meta tags (title, description, canonical, Open Graph,
 * Twitter Card, and optional BreadcrumbList structured data) into the document
 * <head> for pages that are not the main index.html (Terms, Privacy, etc.).
 * Cleans up injected tags on unmount to avoid leaking between page navigations.
 */
export default function SeoMeta({ title, description, path, breadcrumbs, noIndex }) {
  useEffect(() => {
    const url = `https://gbicsa.com${path}`;
    const tags = [];

    const set = (attr, key, content) => {
      const el = document.createElement("meta");
      el.setAttribute(attr, key);
      el.setAttribute("content", content);
      document.head.appendChild(el);
      tags.push(el);
    };

    const link = document.createElement("link");
    link.rel = "canonical";
    link.href = url;
    document.head.appendChild(link);
    tags.push(link);

    document.title = title;

    set("name", "description", description);
    if (noIndex) set("name", "robots", "noindex, nofollow");
    set("property", "og:title", title);
    set("property", "og:description", description);
    set("property", "og:url", url);
    set("property", "og:type", "website");
    set("name", "twitter:card", "summary_large_image");
    set("name", "twitter:title", title);
    set("name", "twitter:description", description);

    let scriptEl = null;
    if (breadcrumbs && breadcrumbs.length > 0) {
      scriptEl = document.createElement("script");
      scriptEl.type = "application/ld+json";
      scriptEl.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: `https://gbicsa.com${b.path}`,
        })),
      });
      document.head.appendChild(scriptEl);
    }

    return () => {
      tags.forEach((t) => t.remove());
      if (scriptEl) scriptEl.remove();
    };
  }, [title, description, path, breadcrumbs, noIndex]);

  return null;
}